import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { usePostHog } from "@posthog/react";
import { TRACK_EVENTS } from "@payfac/constants";

import { LEAVE_DURATION } from "./pendencies-carousel.constants";
import type {
  UsePendenciesCarouselArgs,
  TransitionState,
  CarouselError,
} from "./pendencies-carousel.type";

const buildKey = (e: CarouselError) =>
  `${e.installment}-${e.brand}-${e.captureType}`;

export const usePendenciesCarousel = ({
  errors,
  isLoading,
}: UsePendenciesCarouselArgs) => {
  const posthog = usePostHog();

  const [queue, setQueue] = useState<CarouselError[]>([]);
  const [decided, setDecided] = useState<
    Array<{ item: CarouselError; decision: "accept" | "skip" }>
  >([]);
  const [transitionState, setTransitionState] = useState<TransitionState>(null);
  const [done, setDone] = useState(false);

  const timeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const trackTimeout = (callback: () => void, ms: number) => {
    const id = setTimeout(() => {
      timeoutsRef.current = timeoutsRef.current.filter((t) => t !== id);
      callback();
    }, ms);
    timeoutsRef.current.push(id);
  };

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = [];
    };
  }, []);

  const total = errors?.length ?? 0;
  const accepted = useMemo(
    () => decided.filter((d) => d.decision === "accept").map((d) => d.item),
    [decided],
  );
  const skipped = useMemo(
    () => decided.filter((d) => d.decision === "skip").map((d) => d.item),
    [decided],
  );
  const totalImpact = useMemo(
    () => accepted.reduce((s, e) => s + (e.currentValue ?? 0), 0),
    [accepted],
  );

  useEffect(() => {
    if (!errors) return;
    const enriched: CarouselError[] = errors.map((e) => ({
      ...e,
      key: buildKey(e as CarouselError),
    }));
    const previouslyAccepted = enriched.filter((e) => !!e.consentedAt);
    const pending = enriched.filter((e) => !e.consentedAt);

    setQueue(pending);
    setDecided(
      previouslyAccepted.map((item) => ({ item, decision: "accept" as const })),
    );
    setTransitionState(null);
    setDone(pending.length === 0 && enriched.length > 0);
  }, [errors]);

  const resolve = useCallback(
    (decision: "accept" | "skip") => {
      if (transitionState) return;
      const [top, ...rest] = queue;
      if (!top) return;

      posthog.capture(TRACK_EVENTS.PENDENCIES.CAROUSEL_SWIPE, {
        decision,
        key: top.key,
        remaining: rest.length,
      });

      setTransitionState(decision);

      trackTimeout(() => {
        setDecided((d) => [...d, { item: top, decision }]);
        setQueue(rest);
        setTransitionState(null);
        if (rest.length === 0) {
          trackTimeout(() => setDone(true), 120);
        }
      }, LEAVE_DURATION);
    },
    [transitionState, queue, posthog],
  );

  const undo = useCallback(() => {
    if (decided.length === 0 || transitionState) return;
    const last = decided[decided.length - 1];
    if (last.item.consentedAt) return;
    setDecided((d) => d.slice(0, -1));
    setQueue((q) => [last.item, ...q]);
    if (done) setDone(false);
  }, [decided, transitionState, done]);

  useEffect(() => {
    if (done || isLoading) return;
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;

      const key = e.key.toLowerCase();
      const isZ = key === "z";
      const isCmdOrCtrl = e.metaKey || e.ctrlKey;

      if (isCmdOrCtrl && isZ) {
        e.preventDefault();
        undo();
      } else if (key === "arrowright" || key === "enter") {
        e.preventDefault();
        resolve("accept");
      } else if (key === "arrowleft") {
        e.preventDefault();
        resolve("skip");
      }
    };
    globalThis?.addEventListener("keydown", handler, true);
    return () => globalThis?.removeEventListener("keydown", handler, true);
  }, [done, isLoading, resolve, undo]);

  const handleReview = useCallback(() => {
    if (!errors) return;
    const enriched: CarouselError[] = errors.map((e) => ({
      ...e,
      key: buildKey(e as CarouselError),
    }));
    const previouslyAccepted = enriched.filter((e) => !!e.consentedAt);
    const pending = enriched.filter((e) => !e.consentedAt);
    setQueue(pending.length > 0 ? pending : enriched);
    setDecided(
      pending.length > 0
        ? previouslyAccepted.map((item) => ({
            item,
            decision: "accept" as const,
          }))
        : [],
    );
    setDone(false);
  }, [errors]);

  const remaining = queue.length;
  const current = queue[0];
  const currentIdx = total - remaining + 1;
  const progressPct = total === 0 ? 0 : ((total - remaining) / total) * 100;
  const canUndo =
    decided.length > 0 && !decided[decided.length - 1].item.consentedAt;

  const removeItem = useCallback(
    (key: string) => {
      if (transitionState) return;

      const foundIdx = decided.findIndex((d) => d.item.key === key);
      if (foundIdx === -1) return;

      const found = decided[foundIdx];

      setDecided((d) => d.filter((_, i) => i !== foundIdx));
      setQueue((q) => [found.item, ...q]);
      if (done) setDone(false);
    },
    [transitionState, decided, done],
  );

  return {
    queue,
    accepted,
    skipped,
    transitionState,
    done,
    total,
    current,
    currentIdx,
    progressPct,
    canUndo,
    totalImpact,
    remaining,
    resolve,
    undo,
    removeItem,
    handleReview,
  };
};
